import React, { useState, useCallback } from 'react';

import classNames from 'classnames';
import Image from 'next/image';

import Button from '@/components/Button';
import { BackButton } from '@/components/PredefinedButtons';
import { WizardStep } from '@/components/Wizard';

import { appUiIds } from '@/constants/appUiIds';
import useAppUiData from '@/hooks/useAppUiData';

import styles from './ClaimBookingStep.module.scss';
import checkIcon from './images/check.svg';
import { ContactMethod, StepData } from '../types/ClaimBookingTypes';

type ClaimBookingOtpMethodSelectProps = {
  tabId: string;
  data: StepData;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (value: StepData & { selectedMethod: ContactMethod }) => void;
  onBack: () => void;
  onCancel: () => void;
};

const ClaimBookingOtpMethodSelect: React.FC<ClaimBookingOtpMethodSelectProps> = ({
  tabId,
  data,
  onSubmit,
  onBack,
  onCancel,
}) => {
  const appUi = useAppUiData();
  const [selectedMethod, setSelectedMethod] = useState<ContactMethod | null>(null);
  const [methodSelectError, setMethodSelectError] = useState<boolean | null>(null);

  const availableMethods: ContactMethod[] = [];
  const { contactDetails } = data;

  if (contactDetails?.email || contactDetails?.hasEmail) {
    const email = contactDetails?.email || '';
    availableMethods.push({
      type: 'email',
      value: email,
      displayValue: contactDetails?.emailMasked || email || '',
    });
  }

  if (contactDetails?.phone || contactDetails?.hasPhone) {
    const phone = contactDetails?.phone || '';
    availableMethods.push({
      type: 'phone',
      value: phone,
      displayValue: contactDetails?.phoneMasked || '',
    });
  }

  const handleChange = (method: ContactMethod) => {
    setMethodSelectError(null);
    setSelectedMethod(method);
  };

  const handleTabNavigation = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Tab') return;
    const container = e.currentTarget.closest<HTMLElement>('[data-radio-group]');
    if (!container) return;
    const radios = Array.from(container.querySelectorAll<HTMLInputElement>('input[type="radio"]:not(:disabled)'));
    const focusedIndex = radios.findIndex((r) => r === document.activeElement);
    if (focusedIndex === -1) return;
    if (!e.shiftKey && focusedIndex < radios.length - 1) {
      e.preventDefault();
      radios[focusedIndex + 1].focus();
    } else if (e.shiftKey && focusedIndex > 0) {
      e.preventDefault();
      radios[focusedIndex - 1].focus();
    }
  }, []);

  const handleSubmit = () => {
    if (!selectedMethod) {
      setMethodSelectError(true);

      return;
    }

    onSubmit({
      ...data,
      selectedMethod,
      otpAttempts: 0,
    });
  };

  return (
    <WizardStep tabId={tabId}>
      <BackButton onClick={onBack} className={styles.backButton} />
      <h2 className={styles.title}>{appUi[appUiIds.claimBooking.passcodeHeading]}</h2>
      <form className={styles.selectForm}>
        <div className={styles.radioGroup} data-radio-group>
          {availableMethods?.map((method) => {
            const isSelected = selectedMethod?.type === method.type;
            const labelClassName = classNames(styles.otpContactOption, {
              [styles.otpContactOptionSelected]: isSelected,
              [styles.warningLabel]: methodSelectError,
            });

            return (
              <label key={method.type} className={labelClassName} htmlFor={method.type}>
                <input
                  id={method.type}
                  type="radio"
                  name="otpMethod"
                  value={method.type}
                  checked={isSelected}
                  onChange={() => handleChange(method)}
                  onKeyDown={handleTabNavigation}
                  className={styles.radioInput}
                />
                <span
                  className={classNames(styles.customRadio, { [styles.warningInputIcon]: methodSelectError })}
                  aria-hidden
                >
                  <Image src={checkIcon} alt="" className={styles.checkIcon} width={13} height={12} />
                </span>
                <div className={styles.contactRadioDescription}>
                  <span>
                    {method.type === 'email'
                      ? appUi[appUiIds.claimBooking.emailOptionLabel]
                      : appUi[appUiIds.claimBooking.smsOptionLabel]}
                  </span>
                  <span className={styles.contactDisplayValue}>{method.displayValue}</span>
                </div>
              </label>
            );
          })}
        </div>
        {methodSelectError && <p className={styles.warning}>Select an option</p>}
        <p
          className={styles.contactSelectSubtitle}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: appUi[appUiIds.claimBooking.passcodeBody] }}
        />
        <Button variant="primary" onClick={handleSubmit}>
          {appUi[appUiIds.commonLabels.next]}
        </Button>
        <Button onClick={onCancel} className={styles.cancelLink}>
          {appUi[appUiIds.commonLabels.cancel]}
        </Button>
      </form>
    </WizardStep>
  );
};

export default ClaimBookingOtpMethodSelect;
